import {
    Box,
    Button,
    Card,
    Container,
    Grid,
    InputAdornment,
    LinearProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Chip,
    Tooltip,
    CardActionArea,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    IconButton
} from "@mui/material";
import { 
    Search as SearchIcon, 
    Visibility as VisibilityIcon,
    Close as CloseIcon,
    AccountBalanceWallet as WalletIcon,
    TrendingUp as RewardIcon,
    Event as DateIcon,
    Fingerprint as IdIcon,
    Info as InfoIcon
} from "@mui/icons-material";
import { useState, useMemo } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import useAdminStakingData from "src/hooks/useAdminStakingData";
import getChain from "src/cryptoUtilities/getChain";
import { NETWORK } from "src/config";
import stakingAbi from "src/cryptoUtilities/stakingAbi";
import perpetualStakingAbi from "src/cryptoUtilities/perpetual-staking-abi";
import newStakeAbi from "src/cryptoUtilities/new-stake-abi";

const DashboardWrapper = styled(Box)`
    min-height: 100vh;
    padding: 8rem 0 3rem;
    background-color: #0D0E10;
`;

const HeaderCard = styled(Paper)`
    background-color: #15171C;
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 1.5rem;
    padding: 2rem;
    margin-bottom: 2.5rem;
`;

const ContractWidget = styled(Card, {
    shouldForwardProp: (prop) => prop !== 'active',
})`
    background-color: #15171C;
    border: 2px solid ${props => props.active ? '#00ffa2' : 'rgba(255, 255, 255, 0.03)'};
    border-radius: 1.25rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    height: 100%;
    &:hover {
        transform: translateY(-5px);
        border-color: #00ffa2;
        box-shadow: 0 10px 20px rgba(0, 255, 162, 0.1);
    }
`;

const MetricCard = styled(Paper)`
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 1rem;
    padding: 1.5rem;
    text-align: center;
`;

const StyledTableContainer = styled(TableContainer)`
    background-color: #15171C;
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 1.25rem;
    margin-top: 2rem;
    overflow: hidden;
`;

const Dashboard = () => {
    const navigate = useNavigate();
    
    const selectedChain = useMemo(() => getChain()[NETWORK], []);
    
    // Define the 6 contracts with their metadata
    const contracts = useMemo(() => [
        { name: "Public (New Stake)", address: selectedChain.stakingContract_new_stake, abi: newStakeAbi, type: "new" },
        { name: "Private IPO (New Stake)", address: selectedChain.stakingContract_new_stakeprivate, abi: newStakeAbi, type: "new" },
        { name: "Self Maturing (Public)", address: selectedChain.stakingContract_selfMaturing_apy, abi: perpetualStakingAbi, type: "perpetual" },
        { name: "Self Maturing (Private)", address: selectedChain.stakingContract_selfMaturing_apy_private, abi: perpetualStakingAbi, type: "perpetual" },
        { name: "90-DAY / 35% APY", address: selectedChain.stakingContractAddress_35_apy, abi: stakingAbi, type: "perpetual" },
        { name: "90-DAY / 35% (v2)", address: selectedChain.stakingContractAddress_new_apy, abi: stakingAbi, type: "perpetual" },
    ].filter(c => !!c.address), [selectedChain]);

    const { allStakes, poolData, loading, loadingPool, progress, fetchPoolData } = useAdminStakingData(contracts);
    const [search, setSearch] = useState("");
    const [selectedContract, setSelectedContract] = useState(null);
    const [detailStake, setDetailStake] = useState(null);
    const [isDetailOpen, setIsDetailOpen] = useState(false);

    const handleLogout = () => {
        localStorage.removeItem("admin-token");
        localStorage.removeItem("admin_staking_pool_data");
        navigate("/admin/login");
    };

    const handleWidgetClick = (contract) => {
        setSelectedContract(contract.address);
        // On-demand fetch if data isn't already loaded for this pool
        if (!poolData[contract.address.toLowerCase()]) {
            fetchPoolData(contract);
        }
    };

    const handleOpenDetail = (stake) => {
        setDetailStake(stake);
        setIsDetailOpen(true);
    };

    const handleCloseDetail = () => {
        setIsDetailOpen(false);
    };

    const displayStakes = useMemo(() => {
        let stakes = [];
        if (selectedContract) {
            stakes = poolData[selectedContract.toLowerCase()] || [];
        } else {
            stakes = allStakes;
        }

        if (search) {
            const lowSearch = search.toLowerCase();
            stakes = stakes.filter(s => 
                s.staker.toLowerCase().includes(lowSearch) || 
                s.contractLabel.toLowerCase().includes(lowSearch)
            );
        }
        return stakes;
    }, [allStakes, poolData, selectedContract, search]);

    const metrics = useMemo(() => {
        const totalAmount = displayStakes.reduce((acc, s) => acc + parseFloat(s.amount), 0);
        const uniqueStakers = new Set(displayStakes.map(s => s.staker)).size;
        return { totalAmount, uniqueStakers, count: displayStakes.length };
    }, [displayStakes]);

    return (
        <DashboardWrapper>
            <Container maxWidth="xl">
                <HeaderCard elevation={0}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h4" fontWeight="900" sx={{ color: "#fff", mb: 0.5 }}>
                                Admin Command Center
                            </Typography>
                            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.4)" }}>
                                Select a pool widget below to sync and view individual contract data.
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={2}>
                            <Button 
                                variant="outlined" 
                                color="error" 
                                onClick={handleLogout}
                                sx={{ borderRadius: "0.75rem", fontWeight: 700 }}
                            >
                                Logout
                            </Button>
                        </Stack>
                    </Stack>
                </HeaderCard>

                <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" sx={{ color: "#fff", mb: 2, fontWeight: 700, opacity: 0.8 }}>
                        Staking Pool Selector
                    </Typography>
                    <Grid container spacing={2}>
                        {contracts.map((contract) => {
                            const isLoaded = !!poolData[contract.address.toLowerCase()];
                            const isSelected = selectedContract === contract.address;
                            const isLoadingThis = loadingPool === contract.address;

                            return (
                                <Grid item xs={12} sm={6} md={3} lg={1.7} key={contract.address}>
                                    <ContractWidget 
                                        elevation={0} 
                                        active={isSelected}
                                        sx={{ 
                                            opacity: (isSelected || !selectedContract) ? 1 : 0.6,
                                            position: 'relative',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <CardActionArea 
                                            onClick={() => handleWidgetClick(contract)}
                                            sx={{ p: 2, height: '100%' }}
                                        >
                                            <Typography variant="subtitle2" fontWeight="800" sx={{ color: isSelected ? "#00ffa2" : "#fff", mb: 0.5, fontSize: '0.75rem' }}>
                                                {contract.name}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.3)", fontFamily: 'monospace', display: 'block', mb: 1 }}>
                                                {contract.address.slice(0, 6)}...{contract.address.slice(-4)}
                                            </Typography>
                                            
                                            {isLoaded ? (
                                                <Chip 
                                                    label={`${poolData[contract.address.toLowerCase()].length} Stakes`}
                                                    size="small"
                                                    sx={{ 
                                                        height: 18, 
                                                        fontSize: '0.6rem', 
                                                        backgroundColor: 'rgba(0, 255, 162, 0.1)', 
                                                        color: '#00ffa2',
                                                        border: '1px solid rgba(0, 255, 162, 0.2)'
                                                    }}
                                                />
                                            ) : (
                                                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.2)", fontStyle: 'italic' }}>
                                                    {isLoadingThis ? "Syncing..." : "Not Synced"}
                                                </Typography>
                                            )}
                                        </CardActionArea>
                                        {isLoadingThis && (
                                            <LinearProgress 
                                                variant="determinate" 
                                                value={progress.total > 0 ? (progress.current / progress.total) * 100 : 0}
                                                sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 4, backgroundColor: 'transparent' }}
                                                color="success"
                                            />
                                        )}
                                    </ContractWidget>
                                </Grid>
                            );
                        })}
                        <Grid item xs={12} sm={6} md={3} lg={1.1}>
                            <ContractWidget elevation={0} active={!selectedContract} sx={{ borderStyle: 'dashed' }}>
                                <CardActionArea onClick={() => setSelectedContract(null)} sx={{ p: 2, height: '100%', textAlign: 'center' }}>
                                    <Typography variant="subtitle2" fontWeight="800" sx={{ color: "#fff", fontSize: '0.7rem' }}>
                                        GLOBAL VIEW
                                    </Typography>
                                </CardActionArea>
                            </ContractWidget>
                        </Grid>
                    </Grid>
                </Box>

                {selectedContract && (
                    <Box sx={{ p: 3, backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '1.5rem', border: '1px solid rgba(255,255,255,0.05)', mb: 4 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                            <Box>
                                <Typography variant="h6" fontWeight="800" sx={{ color: "#fff" }}>
                                    {contracts.find(c => c.address === selectedContract)?.name} Details
                                </Typography>
                                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)" }}>
                                    Contract Address: {selectedContract}
                                </Typography>
                            </Box>
                            <Button 
                                variant="contained" 
                                color="success" 
                                size="small"
                                onClick={() => handleWidgetClick(contracts.find(c => c.address === selectedContract))}
                                disabled={loading}
                                sx={{ borderRadius: "0.5rem", fontWeight: 700 }}
                            >
                                {loading ? "Syncing..." : "Re-Sync Pool"}
                            </Button>
                        </Stack>

                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid item xs={12} md={4}>
                                <MetricCard elevation={0}>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontWeight: 700, textTransform: "uppercase" }}>
                                        Total BTAF Staked
                                    </Typography>
                                    <Typography variant="h4" fontWeight="900" sx={{ color: "#00ffa2", mt: 1 }}>
                                        {metrics.totalAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                    </Typography>
                                </MetricCard>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <MetricCard elevation={0}>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontWeight: 700, textTransform: "uppercase" }}>
                                        Unique Wallets
                                    </Typography>
                                    <Typography variant="h4" fontWeight="900" sx={{ color: "#00bcff", mt: 1 }}>
                                        {metrics.uniqueStakers}
                                    </Typography>
                                </MetricCard>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <MetricCard elevation={0}>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontWeight: 700, textTransform: "uppercase" }}>
                                        Wallet Count
                                    </Typography>
                                    <Typography variant="h4" fontWeight="900" sx={{ color: "#f7d060", mt: 1 }}>
                                        {metrics.count}
                                    </Typography>
                                </MetricCard>
                            </Grid>
                        </Grid>

                        <TextField
                            fullWidth
                            placeholder="Filter addresses in this pool..."
                            variant="outlined"
                            size="small"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            sx={{
                                backgroundColor: "rgba(0,0,0,0.2)",
                                borderRadius: "0.75rem",
                                mb: 3,
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "0.75rem",
                                    color: "#fff",
                                }
                            }}
                        />

                        <StyledTableContainer component={Paper}>
                            <Table sx={{ minWidth: 650 }}>
                                <TableHead sx={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
                                    <TableRow>
                                        <TableCell sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 800 }}>Staker Address</TableCell>
                                        <TableCell sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 800 }}>Entry ID</TableCell>
                                        <TableCell sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 800 }}>Start Date</TableCell>
                                        <TableCell sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 800 }}>Amount</TableCell>
                                        <TableCell sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 800 }}>Reward</TableCell>
                                        <TableCell sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 800 }}>Status</TableCell>
                                        <TableCell sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 800 }} align="right">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayStakes.length > 0 ? (
                                        displayStakes.map((stake, idx) => (
                                            <TableRow key={`${stake.staker}-${stake.contractAddress}-${stake.stakeId}-${idx}`} hover sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.02) !important' } }}>
                                                <TableCell sx={{ color: '#fff' }}>
                                                    <Tooltip title={stake.staker}>
                                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                                            {stake.staker.slice(0, 15)}...{stake.staker.slice(-10)}
                                                        </Typography>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell sx={{ color: 'rgba(255,255,255,0.6)' }}>ID-{stake.stakeId}</TableCell>
                                                <TableCell sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                                    {new Date(stake.startTime * 1000).toLocaleDateString()}
                                                </TableCell>
                                                <TableCell sx={{ color: '#00ffa2', fontWeight: 800 }}>{parseFloat(stake.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })} BTAF</TableCell>
                                                <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>{parseFloat(stake.accruedReward || 0).toFixed(4)} BTAF</TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={stake.unstaked ? "Withdrawn" : "Active"} 
                                                        size="small"
                                                        sx={{ 
                                                            borderRadius: "4px", 
                                                            fontWeight: 800, 
                                                            height: 22, 
                                                            fontSize: '0.65rem',
                                                            backgroundColor: stake.unstaked ? "#000" : "rgba(0, 255, 162, 0.1)",
                                                            color: stake.unstaked ? "#fff" : "#00ffa2",
                                                            border: stake.unstaked ? "1px solid #333" : "1px solid rgba(0, 255, 162, 0.2)",
                                                            textTransform: 'uppercase'
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton 
                                                        size="small" 
                                                        sx={{ color: '#00ffa2', backgroundColor: 'rgba(0, 255, 162, 0.05)', '&:hover': { backgroundColor: 'rgba(0, 255, 162, 0.15)' } }}
                                                        onClick={() => handleOpenDetail(stake)}
                                                    >
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={7} sx={{ color: 'rgba(255,255,255,0.3)', textAlign: 'center', py: 8 }}>
                                                {loading ? `Scanning blockchain for stakes (Found: ${progress.current} / ${progress.total})...` : "Select a contract above to begin."}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </StyledTableContainer>
                    </Box>
                )}

                {!selectedContract && (
                    <Paper sx={{ p: 5, textAlign: 'center', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: '1.5rem' }}>
                        <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1 }}>
                            Global Dashboard Overview
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.3)', mb: 3 }}>
                            Aggregate data is available once individual pools are synced.
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <MetricCard elevation={0}>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)" }}>Total Target tokens across all Pools</Typography>
                                    <Typography variant="h4" fontWeight="800" sx={{ color: "#fff", mt: 1 }}>{allStakes.reduce((acc, s) => acc + parseFloat(s.amount), 0).toLocaleString()}</Typography>
                                </MetricCard>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <MetricCard elevation={0}>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)" }}>Total Wallets Synced</Typography>
                                    <Typography variant="h4" fontWeight="800" sx={{ color: "#fff", mt: 1 }}>{new Set(allStakes.map(s => s.staker)).size}</Typography>
                                </MetricCard>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <MetricCard elevation={0}>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)" }}>Total Pools Synced</Typography>
                                    <Typography variant="h4" fontWeight="800" sx={{ color: "#fff", mt: 1 }}>{Object.keys(poolData).length} / {contracts.length}</Typography>
                                </MetricCard>
                            </Grid>
                        </Grid>
                    </Paper>
                )}
            </Container>
            {/* Stake Details Dialog */}
            <Dialog 
                open={isDetailOpen} 
                onClose={handleCloseDetail}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: '#15171C',
                        backgroundImage: 'none',
                        border: '1px solid rgba(255,255,255,0.05)',
                        borderRadius: '1.5rem',
                        color: '#fff'
                    }
                }}
            >
                <DialogTitle sx={{ m: 0, p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="800">Stake Inspector</Typography>
                    <IconButton onClick={handleCloseDetail} sx={{ color: 'rgba(255,255,255,0.3)' }}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.05)', p: 3 }}>
                    {detailStake && (
                        <Grid container spacing={4}>
                            <Grid item xs={12}>
                                <Typography variant="caption" color="rgba(255,255,255,0.4)" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <WalletIcon sx={{ fontSize: '1rem' }} /> STAKER ADDRESS
                                </Typography>
                                <Typography variant="body1" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mt: 0.5, fontWeight: 700 }}>
                                    {detailStake.staker}
                                </Typography>
                            </Grid>

                            <Grid item xs={6}>
                                <Typography variant="caption" color="rgba(255,255,255,0.4)" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <IdIcon sx={{ fontSize: '1rem' }} /> ENTRY IDENTIFIER
                                </Typography>
                                <Typography variant="h6" fontWeight="800" sx={{ mt: 0.5 }}>
                                    ID-{detailStake.stakeId}
                                </Typography>
                            </Grid>

                            <Grid item xs={6}>
                                <Typography variant="caption" color="rgba(255,255,255,0.4)" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DateIcon sx={{ fontSize: '1rem' }} /> START DATE
                                </Typography>
                                <Typography variant="h6" fontWeight="800" sx={{ mt: 0.5 }}>
                                    {new Date(detailStake.startTime * 1000).toLocaleDateString()}
                                </Typography>
                            </Grid>

                            <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(255,255,255,0.03)' }} /></Grid>

                            <Grid item xs={12}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography variant="caption" color="rgba(255,255,255,0.4)">PRINCIPAL AMOUNT</Typography>
                                        <Typography variant="h5" fontWeight="900" sx={{ color: '#00ffa2' }}>{detailStake.amount} BTAF</Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography variant="caption" color="rgba(255,255,255,0.4)">CURRENT REWARDS</Typography>
                                        <Typography variant="h5" fontWeight="900" sx={{ color: '#00bcff' }}>{parseFloat(detailStake.accruedReward).toFixed(4)} BTAF</Typography>
                                    </Box>
                                </Stack>
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{ p: 2, backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '1rem', border: '1px solid rgba(255,255,255,0.03)' }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <InfoIcon sx={{ color: 'rgba(255,255,255,0.3)' }} />
                                        <Box>
                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>CONTRACT & POOL SOURCE</Typography>
                                            <Typography variant="body2" fontWeight="700">{detailStake.contractLabel}</Typography>
                                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'rgba(255,255,255,0.2)' }}>{detailStake.contractAddress}</Typography>
                                        </Box>
                                    </Stack>
                                </Box>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="caption" color="rgba(255,255,255,0.4)">HISTORIC CLAIMED</Typography>
                                <Typography variant="h6" fontWeight="700">{detailStake.totalClaimed} BTAF</Typography>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, backgroundColor: 'rgba(0,0,0,0.1)' }}>
                    <Button onClick={handleCloseDetail} sx={{ color: 'rgba(255,255,255,0.4)', textTransform: 'none', fontWeight: 800 }}>Close Inspector</Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Chip 
                        label={detailStake?.unstaked ? "POSITION WITHDRAWN" : "POSITION ACTIVE"} 
                        sx={{ 
                            backgroundColor: detailStake?.unstaked ? '#000' : 'rgba(0, 255, 162, 0.1)', 
                            color: detailStake?.unstaked ? '#fff' : '#00ffa2',
                            fontWeight: 900,
                            borderRadius: '4px'
                        }} 
                    />
                </DialogActions>
            </Dialog>
        </DashboardWrapper>
    );
};

export default Dashboard;
